import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { Plus, Edit2, Trash2, Upload, X, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = ["Truck", "Package", "Scissors", "Shield", "Car", "Factory", "Heart", "Award"];

const emptySvc = {
  title_sq: "", title_en: "",
  desc_sq: "", desc_en: "",
  icon: "Package", image: "",
  features_sq: [], features_en: [],
  order: 0, active: true,
};

const ServiceForm = ({ service, onClose, onSaved, authHeaders }) => {
  const [data, setData] = useState(service || emptySvc);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!service?.id;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      set("image", res.url);
      toast.success("Foto u ngarkua");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const featuresArr = (lang) => (data[`features_${lang}`] || []).join("\n");
  const setFeatures = (lang, str) => set(`features_${lang}`, str.split("\n").map((l) => l.trim()).filter(Boolean));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.id;
      delete payload.created_at;
      if (isEdit) {
        await axios.put(`${API}/admin/services/${service.id}`, payload, { headers: authHeaders() });
        toast.success("Shërbimi u përditësua");
      } else {
        await axios.post(`${API}/admin/services`, payload, { headers: authHeaders() });
        toast.success("Shërbimi u shtua");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="service-form-modal">
      <form onSubmit={onSubmit} className="bg-white w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isEdit ? "Ndrysho Shërbimin" : "Shto Shërbim"}
          </h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Titulli (SQ) *</label>
              <input data-testid="svc-title-sq" required value={data.title_sq} onChange={(e) => set("title_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Titulli (EN) *</label>
              <input data-testid="svc-title-en" required value={data.title_en} onChange={(e) => set("title_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Përshkrimi (SQ)</label>
              <textarea rows={3} value={data.desc_sq} onChange={(e) => set("desc_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (EN)</label>
              <textarea rows={3} value={data.desc_en} onChange={(e) => set("desc_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Veçoritë (SQ) — një për rresht</label>
              <textarea rows={4} value={featuresArr("sq")} onChange={(e) => setFeatures("sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Features (EN) — one per line</label>
              <textarea rows={4} value={featuresArr("en")} onChange={(e) => setFeatures("en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Ikona</label>
              <select value={data.icon} onChange={(e) => set("icon", e.target.value)} className="w-full border border-zinc-300 px-3 py-2">
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Renditja</label>
              <input type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={data.active} onChange={(e) => set("active", e.target.checked)} />
                <span className="text-sm font-medium">Aktiv</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Foto</label>
            <div className="flex items-start gap-3">
              {data.image ? (
                <div className="w-32 h-20 bg-zinc-100 overflow-hidden border border-zinc-200 flex-shrink-0 relative">
                  <img src={data.image} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("image", "")} className="absolute top-1 right-1 bg-red-600 text-white p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="w-32 h-20 bg-zinc-100 border border-dashed border-zinc-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <input type="text" value={data.image || ""} onChange={(e) => set("image", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 text-sm" placeholder="URL e fotos" />
                <label className="mt-2 inline-flex items-center gap-2 text-xs cursor-pointer text-[#0A0A0A] hover:underline">
                  <Upload className="w-3 h-3" />
                  {uploading ? "Duke ngarkuar..." : "Ngarko foto"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} disabled={uploading} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-zinc-200 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border border-zinc-300 hover:bg-zinc-50">Anulo</button>
          <button data-testid="svc-save" type="submit" disabled={saving} className="flex-1 px-6 py-3 bg-[#0A0A0A] text-white font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminServices = () => {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/services`, { headers: authHeaders() });
      setItems(data);
    } catch { toast.error("Gabim"); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (s) => {
    if (!window.confirm(`Fshi "${s.title_sq}"?`)) return;
    try {
      await axios.delete(`${API}/admin/services/${s.id}`, { headers: authHeaders() });
      toast.success("U fshi");
      load();
    } catch { toast.error("Gabim"); }
  };

  const toggleActive = async (s) => {
    try {
      await axios.put(`${API}/admin/services/${s.id}`, { ...s, active: !s.active }, { headers: authHeaders() });
      load();
    } catch { toast.error("Gabim"); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-services-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Shërbimet</h1>
            <p className="text-zinc-600 mt-1">Menaxho shërbimet e Tamari</p>
          </div>
          <button data-testid="add-service-button" onClick={() => setEditing({})} className="bg-[#0A0A0A] text-white px-5 py-3 font-medium hover:bg-zinc-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Shto Shërbim
          </button>
        </div>

        {loading ? <div className="text-zinc-500">Duke ngarkuar...</div> : (
          <div className="bg-white border border-zinc-200">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-600">
                <tr>
                  <th className="p-4">Foto</th>
                  <th className="p-4">Titulli</th>
                  <th className="p-4">Ikona</th>
                  <th className="p-4">Renditja</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="p-4">
                      <div className="w-16 h-10 bg-zinc-100 overflow-hidden">
                        {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-[#0A0A0A]">{s.title_sq}</p>
                      <p className="text-xs text-zinc-500">{s.title_en}</p>
                    </td>
                    <td className="p-4 text-sm">{s.icon}</td>
                    <td className="p-4 text-sm">{s.order}</td>
                    <td className="p-4">
                      <button onClick={() => toggleActive(s)} className={`text-xs px-2 py-1 inline-flex items-center gap-1 ${s.active ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                        {s.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {s.active ? "Aktiv" : "I fshehur"}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditing(s)} className="p-2 hover:bg-zinc-100"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => onDelete(s)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Asnjë shërbim</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {editing !== null && (
          <ServiceForm service={editing.id ? editing : null} authHeaders={authHeaders} onClose={() => setEditing(null)} onSaved={load} />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminServices;
